import classNames from "classnames/dedupe";
import React, { FC } from "react";
import { deepClone } from "../../../helpers";

export type TColumn =
  | number
  | boolean
  | { colSize?: number | boolean; colOffset?: number; colOrder?: number };

export type TAlignCol =
  | "align-self-start"
  | "align-self-center"
  | "align-self-end";

export type TBreakpoint =
  | "xxs"
  | "xms"
  | "ms"
  | "xs"
  | "sm"
  | "md"
  | "lg"
  | "lgm"
  | "xl"
  | "xxl"
  | "xxxl";

export type TBreakpoints = Record<TBreakpoint, TColumn>;

export interface IColumnProps extends Partial<TBreakpoints> {
  as?: React.ElementType;
  classes?: {
    root?: string;
  };
  align?: TAlignCol;
  [key: string]: unknown;
}

export type TBreakpointConfig = {
  colSize?: number | boolean;
  colOffset?: number;
  colOrder?: number;
};

export const BREAKPOINTS: TBreakpoint[] = [
  "xxxl",
  "xxl",
  "xl",
  "lg",
  "md",
  "sm",
  "xs",
];
export const COLUMN_PREFIX = "col";
export const ORDER_PREFIX = "order";
export const OFFSET_PREFIX = "offset";

export const GRID_BREAKPOINTS: Record<TBreakpoint, number> = {
  xxs: 0,
  xms: 375,
  ms: 414,
  xs: 480,
  sm: 576,
  md: 768,
  lg: 992,
  lgm: 1024,
  xl: 1280,
  xxl: 1440,
  xxxl: 1680,
};

const Column: FC<IColumnProps> = ({
  classes,
  as: Component = "div",
  align,
  children,
  ...other
}) => {
  const columnSizes: string[] = [];
  let computedClasses: string[] = [];

  Object.keys(deepClone(other))
    .filter((key) => BREAKPOINTS.includes(key as any))
    .forEach((breakpoint) => {
      const breakpointConfig = other[breakpoint];
      // eslint-disable-next-line no-param-reassign
      delete other[breakpoint];

      let colSize: TBreakpointConfig["colSize"];
      let colOffset: TBreakpointConfig["colOffset"];
      let colOrder: TBreakpointConfig["colOrder"];

      // Could be an object or just a size value
      if (typeof breakpointConfig === "object" && breakpointConfig != null) {
        ({
          colSize = true,
          colOffset,
          colOrder,
        } = breakpointConfig as TBreakpointConfig);
      } else {
        colSize = breakpointConfig as TBreakpointConfig["colSize"];
      }

      const breakpointName = breakpoint !== "xs" ? `-${breakpoint}` : "";

      if (colOrder != null)
        computedClasses.push(`${ORDER_PREFIX}${breakpointName}-${colOrder}`);
      if (colOffset != null)
        computedClasses.push(`${OFFSET_PREFIX}${breakpointName}-${colOffset}`);

      if (colSize)
        columnSizes.push(
          colSize === true
            ? `${COLUMN_PREFIX}${breakpointName}`
            : `${COLUMN_PREFIX}${breakpointName}-${colSize}`,
        );
    });

  if (!columnSizes.length) {
    columnSizes.push(COLUMN_PREFIX); // plain 'col'
  }

  computedClasses = [...computedClasses, ...columnSizes]; // merge classes

  const rootClassName = classNames(classes?.root, align, ...computedClasses);

  return (
    <Component className={rootClassName} {...other}>
      {children}
    </Component>
  );
};

export default Column;
